import React from 'react';
import {PropTypes} from 'prop-types';
import moment from 'moment-timezone';
import valid from 'card-validator';
import creditCardType from 'credit-card-type';
import {PropTypesEx} from './proptypes.extensions';

import {BaseFormComponent} from './baseFormComponent';
import {ButtonBusyIndicatorComponent} from './buttonBusyIndicatorComponent';
import {SetFocusOnFirstFormControl, AssignProperties, ToString, NormalizeClassNameString, ObjectHasProperty} from './utils';
import {ItemPopupDescriptionComponent} from './itemPopupDescriptionComponent';

export function getCreditCardBrandNameByNumber(cardNumber) {
    let brandName = '';

    if (Boolean(cardNumber)) {
        const test = valid.number(cardNumber);

        if ((test.isValid || test.isPotentiallyValid) && Boolean(test.card)) {
            brandName = test.card.niceType;
        }
    }

    const result = ToString(brandName || '').trim();

    return result;
}

export function getCreditCardTypeByBrandName(brandName) {
    brandName = ToString(brandName).trim().toLowerCase();

    let result = '';

    switch (brandName) {
        case 'mastercard':
            result = 'master-card';
            break;

        case 'americanexpress':
        case 'amex':
            result = 'american-express';
            break;

        case 'dinersclub':
        case 'diners':
            result = 'diners-club';
            break;

        default:
            result = brandName.replace(' ', '-');
            break;
    }

    return result;
}

export function getCreditCardTypeInfoByBrandName(brandName) {
    const cardType = getCreditCardTypeByBrandName(brandName);

    const result = creditCardType.getTypeInfo(cardType) || 
        {
            niceType: 'Unknown',
            type: '',
            gaps: [ 4, 8, 12 ],
            lengths: [16],
            code: { name: '', size: 4 }            
        };

    return result;
}

export function getCreditCardTypeInfoByNumber(cardNumber) {
    const brandName = getCreditCardBrandNameByNumber(cardNumber);

    const result = getCreditCardTypeInfoByBrandName(brandName);

    return result;
}

export class PayPalButtonComponent extends React.Component {
    render() {
        const self = this;

        const props = {
            ...self.props,
            type: 'button',
        };

        const {busy, caption, visible} = props;

        if (!visible) {
            return null;
        }

        props.disabled = props.disabled || props.busy;
        if (Boolean(props['data-ga-action'])) {
            props['data-ga-action'] = 'click';
        }

        delete props.busy;
        delete props.caption;
        delete props.visible;

        return (
            <button {...props}>
                {caption}&nbsp;&nbsp;<i className="paypal-logo" />
                <ButtonBusyIndicatorComponent visible={busy}/>
            </button>
        )
    }
}
PayPalButtonComponent.propTypes = {
    busy: PropTypes.bool.isRequired,
    visible: PropTypes.bool,
    disabled: PropTypes.bool,
    caption: PropTypes.string.isRequired,
    className: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired
}
PayPalButtonComponent.defaultProps = {
    disabled: false,
    visible: false,
    caption: 'Pay with',
    className: 'btn btn-outline-primary btn-sm btn-paypal newstyle'
}

export class CreditCardCaptionComponent extends React.Component {
    constructor(props, ...others) {
        super(props, ...others);

        this.getCardBrandName = this.getCardBrandName.bind(this);
        this.getIconClass = this.getIconClass.bind(this);
    }

    getCardBrandName() {
        const self = this;
        const result = self.props.cardBrandName || getCreditCardBrandNameByNumber(self.props.cardNumber);

        return result;
    }

    getIconClass() {
        const cardType = getCreditCardTypeByBrandName(this.getCardBrandName());

        let result = '';
        switch (cardType) {
            case 'visa': 
                result = 'fa fa-cc-visa';
                break;
                
            case 'master-card': 
                result = 'fa fa-cc-mastercard'; 
                break;

            case 'american-express': 
                result = 'fa fa-cc-amex'; 
                break;

            case 'diners-club': 
                result = 'fa fa-cc-diners-club';
                break;

            case 'discover': 
                result = 'fa fa-cc-discover'; 
                break;

            case 'jcb': 
                result = 'fa fa-cc-jcb';
                break;

            default:
                result = 'fa fa-credit-card-alt';
                break;
        }

        return result;
    }

    render() {
        const self = this;
        const {cardNumber, showIcon, showNumber, showBrand, showBrackets} = self.props;

        const brandName = self.getCardBrandName();
        const needRender = 
            Boolean(cardNumber) && showNumber ||
            Boolean(brandName) && (showIcon || showBrand);

        if (!needRender) {
            return null;
        }

        const cardNum = cardNumber.padStart(4, 'X').substr(-4);

        return (
            <span className="creditcard-caption">
                {showBrackets ? '(' : ''}
                {showIcon &&
                    <span className={'creditcard-icon ' + self.getIconClass()} title={showBrand ? '' : brandName}>
                        {(showBrand || showNumber) ? <span>&nbsp;</span> : ''}
                    </span>
                }
                {showBrand &&
                    <span className="creditcard-brand">{brandName}
                        {showNumber ? <span>&nbsp;</span> : ''}
                    </span>
                }
                {showNumber && <span className="creditcard-number">{cardNum}</span>}
                {showBrackets ? ')' : ''}
            </span>
        );
    }
}
CreditCardCaptionComponent.propTypes = {
    cardNumber: PropTypes.string.isRequired,
    cardBrandName: PropTypes.string,
    showIcon: PropTypes.bool,
    showNumber: PropTypes.bool,
    showBrand: PropTypes.bool,
    showBrackets: PropTypes.bool
}
CreditCardCaptionComponent.defaultProps = {
    cardNumber: '',
    cardBrandName: '',
    showIcon: true,
    showNumber: true,
    showBrand: false,
    showBrackets: false
}

export class CreditCardFormComponent extends BaseFormComponent {
    constructor(props, ...others) {
        super(props, ...others);

        this.strings = {...this.constructor.defaultProps.strings};
        AssignProperties(this.strings, props.strings);
        this.getFormData = this.getFormData.bind(this);

        this._valueHandler.onChangeOptions = {
            'CardNumber': {doLTrim: true},
        }
    }

    componentDidMount() {
        const self = this;

        if ((self.props.setFocus)) {
            SetFocusOnFirstFormControl(self, true);
        }
    }

    getFormData() {
        const self = this;
        const result = {...self.constructor.defaultProps.data, ...self.props.data};

        return result;
    }

    renderCardNumberEditor() {
        const self = this;
        const {disabled, errors, cardBrandName} = self.props;
        const formData = self.getFormData();
        const strings = self.strings;
        const cardNumber = formData.CardNumber || '';

        return (
            <div className="form-group newstyle">
                <input 
                    type="text" 
                    id="CardNumber"
                    name="CardNumber" 
                    autoComplete="off"
                    onChange={self.handleInputChange}
                    className={'form-control ' + self.getControlClass('CardNumber', errors)} 
                    value={cardNumber} 
                    disabled={disabled} 
                />
                <label htmlFor="CardNumber">
                    <span className="text-truncate mre-1">{strings.F_CardNumber}</span>
                    <CreditCardCaptionComponent 
                        cardNumber={cardNumber} 
                        cardBrandName={cardBrandName} 
                        showIcon
                        showNumber={false} 
                        showBrand
                        showBrackets
                    />
                </label>
                <div className="invalid-feedback">{self.getControlError('CardNumber', errors)}</div>
            </div>
        );
    }

    render() {
        const self = this;
        const {disabled, errors} = self.props;
        const formData = self.getFormData();
        const strings = self.strings;

        const months = moment.monthsShort().map((item, index) => {
			return (<option key={'m-' + index} value={index + 1}>{index + 1} &mdash; {item}</option>);
        });

        const years = [];
        const currYear = moment().year();
        for(var y = currYear; y <= currYear + 20; y++){
            years.push(<option key={'y-' + y} value={y}>{y}</option>);
        }
        if (formData.ExpiryYear < currYear) {
            years.unshift(<option key={'y-' + formData.ExpiryYear} value={formData.ExpiryYear}>{formData.ExpiryYear}</option>)
        }
        if (formData.ExpiryYear > currYear + 20) {
            years.push(<option key={'y-' + formData.ExpiryYear} value={formData.ExpiryYear}>{formData.ExpiryYear}</option>)
        }

        const expiryDate = moment.utc([formData.ExpiryYear, formData.ExpiryMonth - 1]).endOf('month');
        const isExpired = moment.utc().isAfter(expiryDate);

        return (
            <div id="creditcard-block">
                <div className="form-group newstyle">
                    <input 
                        type="text" 
                        id="HolderName"
                        name="HolderName" 
                        onChange={(e) => self.handleInputChange(e, {doLTrim: true})}
                        className={'form-control ' + self.getControlClass('HolderName', errors)} 
                        value={formData.HolderName || ''} 
                        disabled={disabled} 
                    />
                    <label htmlFor="HolderName"><span className="text-truncate">{strings.F_HolderName}</span></label>
                    <div className="invalid-feedback">{self.getControlError('HolderName', errors)}</div>
                </div>
                {self.renderCardNumberEditor()}
                <div className="form-row">
                    <div className="form-group col newstyle" style={{maxWidth: '10em'}}>
                        <select 
                            id="ExpiryMonth"
                            name="ExpiryMonth" 
                            onChange={self.handleInputChange}
                            className={'custom-select ' + self.getControlClass('ValidThru', errors) + (isExpired ? ' is-warning' : '')} 
                            value={formData.ExpiryMonth || ''} 
                            disabled={disabled}
                        >
                            {months}
                        </select>
                        <label htmlFor="ExpiryMonth"><span className="text-truncate">{strings.F_ValidThru}</span></label>
                        <div className="invalid-feedback text-nowrap">{self.getControlError('ValidThru', errors)}</div>
                    </div>
                    <div className="form-group col newstyle" style={{maxWidth: '6em'}}>
                        <select 
                            id="ExpiryYear"
                            name="ExpiryYear" 
                            onChange={self.handleInputChange}
                            className={'custom-select ' + self.getControlClass('ValidThru', errors) + (isExpired ? ' is-warning' : '')} 
                            value={formData.ExpiryYear || ''} 
                            disabled={disabled}
                        >
                            {years}
                        </select>
                        <label htmlFor="ExpiryYear" style={{background: 'none'}}>&nbsp;</label>
                    </div>
                </div>
            </div>
        );
    }
}
CreditCardFormComponent.propTypes = {
    strings: PropTypes.object,
    data: PropTypes.shape({
        HolderName: PropTypes.string,
        CardNumber: PropTypes.string,
        ExpiryMonth: PropTypesEx.integer.inRange([1, 12]).isStringAllowed,
        ExpiryYear: PropTypesEx.integer.inRange([2000]).isStringAllowed,
    }),
    cadBrandName: PropTypes.string,
    secureCodeNeeded: PropTypes.bool,
    setFocus: PropTypes.bool,
    disabled: PropTypes.bool,
}
CreditCardFormComponent.defaultProps = {
    strings: {
        F_HolderName: 'Holder Name',
        PH_HolderName: 'Holder Name',
        F_CardNumber: 'Card Number',
        PH_CardNumber: 'Card Number',
        F_ValidThru: 'Valid Thru',
        PH_ValidThru: 'Valid Thru',
    },
    data: {
        HolderName: '',
        CardNumber: '',
        ExpiryMonth: null,
        ExpiryYear: null
    },
    cadBrandName: '',
    secureCodeNeeded: true,
    setFocus: false,
    disabled: false
}
CreditCardFormComponent.handledErrors = 'HolderName CardNumber ValidThru';

export class CreditCardSecureCodeComponent extends React.Component {
    constructor(props, ...others) {
        super(props, ...others);
        this.strings = {...this.constructor.defaultProps.strings};
        AssignProperties(this.strings, props.strings);
    }
    
    render() {
        const self = this;
        const {likePassword} = self.props;
        const strings = self.strings;
        const props = {...self.props};

        props.className = NormalizeClassNameString('form-control d-inline-block mr-2 ' + (props.className || ''));
        props.maxLength = props.secureCodeSize;
        props.style = {...props.style, maxWidth: '4em'};

        delete props.secureCodeSize;
        delete props.likePassword;
        delete props.strings;

        return (
            <div>
                <input
                    type={likePassword ? 'password' : 'text'}
                    autoComplete="off"
                    {...props}
                />
                <ItemPopupDescriptionComponent
                    placement="right"
                    popoverClassName="popover-white"
                    className="text-secondary"
                    title={strings.Q_SecureCode}
                    text={strings.HTML_SecureCodeInfo}
                >
                    <span className="small">{strings.Q_SecureCode}</span>
                </ItemPopupDescriptionComponent>
            </div>
        );
    }
}
CreditCardSecureCodeComponent.propTypes = {
    strings: PropTypes.object,
    secureCodeSize: PropTypes.oneOf([3,4]), 
    likePassword: PropTypes.bool.isRequired,
    onChange: PropTypes.func.isRequired,
    name: PropTypes.string.isRequired,
    value: PropTypes.string,
}
CreditCardSecureCodeComponent.defaultProps = {
    strings: {
        Q_SecureCode: 'What is the Secure code?',
        HTML_SecureCodeInfo: '<p>The Secure code is a 3 or 4 digit code printed on your credit card.</p>'
    },
    likePassword: false,
    name: 'SecoureCode',
    value: ''
}
